import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ButtonGroupTypes } from 'devextreme-react/button-group';
import {
    MoneyBox,
    NumberBox,
    DropDownBox,
    // StateDropDown,
    DateTimeBox,
    YesNoToggleButton,
    CheckButton,
    ToggleButtonLabelSize,
} from '@apply/components';
import { calculateMaxLoan } from './calculations';
import type { EstimatorInput } from './types';
import {
    AFFORDABILITY_OPTIONS,
    LOAN_CAPS,
    POP_DENSITY_OPTIONS,
    POP_GROWTH_OPTIONS,
    UNAVAILABLE_STATES,
} from './constants';
import './EstimatorPage.scss';
import { isMobileScreen } from '@apply/ui';
import UsaStates, { IStateResult } from 'usa-states';

// ── Formatters ────────────────────────────────────────────────────────────────

const fmt  = (n: number) =>
    n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const pct  = (n: number) => `${(n * 100).toFixed(1)}%`;
const fmtS = (n: number) => (isFinite(n) ? fmt(n) : 'n/a');

// ── Types ─────────────────────────────────────────────────────────────────────

type Affordability = EstimatorInput['affordability'];
type PopDensity    = EstimatorInput['populationDensity'];
type PopGrowth     = EstimatorInput['populationGrowth'];

export interface EstimatorPageProps {
    // When true, render the full scenario table, metrics, and adjustments
    // (admin / internal use). When false (default), render only the max loan
    // amount hero — the customer-facing apply view.
    showFullResults?: boolean;
}

// ── Query-string prefill ──────────────────────────────────────────────────────

const _qs = new URLSearchParams(window.location.search);
const qStr = (k: string, fb = '') => _qs.get(k) ?? fb;
const qNum = (k: string): number | undefined  => { const v = _qs.get(k); return v !== null ? +v : undefined; };
const qBool = (k: string): boolean | undefined => { const v = _qs.get(k); return v === 'true' ? true : v === 'false' ? false : undefined; };
const qEnum = <T extends string>(k: string): T | undefined => { const v = _qs.get(k); return v ? v as T : undefined; };
const qDate = (k: string): Date | undefined => getValidDateOrUndefined(_qs.get(k));
const getValidDateOrUndefined = (s: string | null): Date | undefined => {
    if (!s) return undefined;
    const d = new Date(s);
    return isNaN(d.getTime()) ? undefined : d;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const ynClick =
    (setter: (v: boolean) => void) =>
    (e: ButtonGroupTypes.ItemClickEvent) => {
        setter((e.itemData as { id: string }).id === 'Yes');
        };
    
const isMobile = isMobileScreen();

const usaStates: IStateResult[] = new UsaStates.UsaStates().states;

// ── Results panel ─────────────────────────────────────────────────────────────

function LoanHero({
    label,
    amount,
    maxCapHitAmount,
    footnote,
}: {
    label: string;
    amount: number;
    maxCapHitAmount?: number | null;
    footnote?: string | null;
}) {
    const formatMillions = (value: number): string => {
        return `$${(value / 1_000_000).toFixed(1)} MM`;
    };
    return (
        
        <div className='est-loan-hero'>
            <div className='est-loan-label'>{label}{!!footnote && '*'}</div>
            <div className='est-loan-amount'>{fmt(amount)}</div>
            { !!maxCapHitAmount && (
                <span className={`est-maxCapHitAmount-pill`}>Max size is {formatMillions(maxCapHitAmount)}</span>
            )}
            { !!footnote && (
                <div className='est-loan-footnote'>*{footnote}</div>
            )}
        </div>
    );
}

function ResultsPanel({ result, nonZeroAdj, showFullResults, qualificationsMet }: {
    result: ReturnType<typeof calculateMaxLoan> | null;
    nonZeroAdj: NonNullable<ReturnType<typeof calculateMaxLoan>>['adjustments'];
    showFullResults: boolean;
    qualificationsMet: boolean;
}) {
    // Loan Estimate results container nested scroll
    // (aware of window and container height)
    const panelRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 900px)');
        const panel = panelRef.current;
        if (!panel) return;

        let raf = 0;
        const measure = () => {
            raf = 0;
            if (mq.matches) {
                panel.style.maxHeight = '';
                return;
            }
            const top = panel.getBoundingClientRect().top;
            panel.style.maxHeight = `${Math.max(0, window.innerHeight - top - 16)}px`;
        };
        const schedule = () => {
            if (!raf) raf = requestAnimationFrame(measure);
        };

        schedule();
        document.addEventListener('scroll', schedule, { capture: true, passive: true });
        window.addEventListener('resize', schedule);
        mq.addEventListener('change', schedule);
        return () => {
            if (raf) cancelAnimationFrame(raf);
            document.removeEventListener('scroll', schedule, { capture: true } as EventListenerOptions);
            window.removeEventListener('resize', schedule);
            mq.removeEventListener('change', schedule);
        };
    }, []);

    return (
        <div
            ref={panelRef}
            className={`est-results ${showFullResults ? 'est-results--full' : 'est-results--minimal'}`}
        >
            <div className='est-results-header'>
                <div className='est-results-title'>Loan Estimate</div>
            </div>
            <div className='est-results-body'>
                {!result ? (
                    <div className='est-results-empty'>
                        Complete all required fields&nbsp;(*)<br />to see your estimate.
                    </div>
                ) : !qualificationsMet ? (
                    <div className='est-results-empty'>
                        Qualifications are not met. Please reach out to a BFI representative.
                    </div>
                ) : (
                    <>
                        <div className='est-loan-heroes'>
                            <LoanHero
                                label='Construction Loan'
                                amount={result.maxLoanAmount}
                                maxCapHitAmount={result.maxLoanAmount >= LOAN_CAPS.default ? LOAN_CAPS.default : null}
                            />
                            <LoanHero
                                label='Line of Credit'
                                amount={result.lineOfCredit.maxLoanAmount}
                                maxCapHitAmount={result.lineOfCredit.maxLoanAmount >= LOAN_CAPS.lineOfCredit ? LOAN_CAPS.lineOfCredit : null}
                                footnote='Based on 74% max. LTV up to $2.5 MM value, reduced LTVs at certain sizes below possible.'
                            />
                        </div>

                        {showFullResults && (
                            <>
                                <div className='est-scenario-block'>
                                    <div className='est-scenario-title'>Construction Loan</div>
                                    <table className='est-scenarios'>
                                        <tbody>
                                            {(
                                                [
                                                    { id: 'C3', label: 'LTV',              val: result.c3_ltv,       rate: pct(result.effectiveLtv) },
                                                    { id: 'C4', label: 'LTC',              val: result.c4_ltc,       rate: pct(result.effectiveLtc) },
                                                    { id: 'C5', label: 'Subordinated Lot', val: result.c5_subLand,   rate: null },
                                                    { id: 'C6', label: 'Loan Cap',         val: result.c6_cap,       rate: null },
                                                    { id: 'C8', label: 'LTV Size Limit',   val: result.c8_sizeLimit, rate: null },
                                                    { id: 'C9', label: 'Other',            val: result.c9_other,     rate: null },
                                                ] as const
                                            ).map(({ id, label, val, rate }) => {
                                                const isBinding = isFinite(val) && val === result.maxLoanAmount;
                                                return (
                                                    <tr key={id} className={isBinding ? 'binding' : ''}>
                                                        <td className='cell-label'>{label}</td>
                                                        <td className='cell-money'>{fmtS(val)}</td>
                                                        <td className='cell-rate'>{rate ?? ''}</td>
                                                    </tr>
                                                );
                                            })}
                                            <tr className='loan-total'>
                                                <td className='cell-label'>LOAN</td>
                                                <td className='cell-money'>{fmt(result.maxLoanAmount)}</td>
                                                <td className='cell-rate'></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div className='est-scenario-block'>
                                    <div className='est-scenario-title'>Line of Credit</div>
                                    <table className='est-scenarios'>
                                        <tbody>
                                            {(
                                                [
                                                    { id: 'M4', label: 'LTV',         val: result.lineOfCredit.ltvCap,  rate: pct(result.lineOfCredit.effectiveLtv) },
                                                    { id: 'M5', label: '100% Cost',   val: result.lineOfCredit.costCap, rate: null },
                                                    { id: 'M6', label: 'Loan Cap',    val: result.lineOfCredit.sizeCap, rate: null },
                                                ] as const
                                            ).map(({ id, label, val, rate }) => {
                                                const isBinding = isFinite(val) && val === result.lineOfCredit.maxLoanAmount;
                                                return (
                                                    <tr key={id} className={isBinding ? 'binding' : ''}>
                                                        <td className='cell-label'>{label}</td>
                                                        <td className='cell-money'>{fmtS(val)}</td>
                                                        <td className='cell-rate'>{rate ?? ''}</td>
                                                    </tr>
                                                );
                                            })}
                                            <tr className='loan-total'>
                                                <td className='cell-label'>LOC</td>
                                                <td className='cell-money'>{fmt(result.lineOfCredit.maxLoanAmount)}</td>
                                                <td className='cell-rate'></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div className='est-metrics'>
                                    <div className='est-metric'>
                                        <span className='mkey'>Experience Level</span>
                                        <span className='mval'>{result.experienceLevel} / 10</span>
                                    </div>
                                    <div className='est-metric'>
                                        <span className='mkey'>Built Score</span>
                                        <span className='mval'>{result.builtScore}</span>
                                    </div>
                                    <div className='est-metric'>
                                        <span className='mkey'>BFI Repayments</span>
                                        <span className='mval'>{result.goodRepayments}</span>
                                    </div>
                                    <div className='est-metric'>
                                        <span className='mkey'>Equity (Construction)</span>
                                        <span className='mval'>{fmt(result.equityRequired)}</span>
                                    </div>
                                    <div className='est-metric'>
                                        <span className='mkey'>Equity (LOC)</span>
                                        <span className='mval'>{fmt(result.lineOfCredit.equityRequired)}</span>
                                    </div>
                                    {(result.isSmallLoan || result.lineOfCredit.isSmallLoan) && (
                                        <div className='est-metric'>
                                            <span className='mkey'>Note</span>
                                            <span className='mval'>Small loan — add'l req. may apply</span>
                                        </div>
                                    )}
                                </div>

                                {nonZeroAdj.length > 0 && (
                                    <div className='est-adjustments'>
                                        <div className='adj-head'>Construction Loan Adjustments</div>
                                        {nonZeroAdj.map((a) => (
                                            <div key={a.label} className='adj-row'>
                                                <span className='adj-label'>{a.label}</span>
                                                <span className={a.value < 0 ? 'adj-neg' : 'adj-pos'}>
                                                    {a.value > 0 ? '+' : ''}{pct(a.value)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

// ── Mobile sticky max-loan bar ────────────────────────────────────────────────

function MobileLoanBar({ result, qualificationsMet }: {
    result: ReturnType<typeof calculateMaxLoan> | null;
    qualificationsMet: boolean;
}) {
    if (!result || !qualificationsMet) return null;
    return (
        <div className='est-mobile-bar'>
            <div className='mb-row'>
                <span className='mb-label'>Construction</span>
                <span className='mb-amount'>{fmt(result.maxLoanAmount)}</span>
            </div>
            <div className='mb-row'>
                <span className='mb-label'>LOC</span>
                <span className='mb-amount'>{fmt(result.lineOfCredit.maxLoanAmount)}</span>
            </div>
        </div>
    );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export const EstimatorPage: React.FC<EstimatorPageProps> = ({ showFullResults = false }) => {
    // ── PROPERTY ─────────────────────────────────────────────────────────────
    const [state, setState] = useState<string | undefined>(qStr('state'));
    const [landValue, setLandValue] = useState<number | undefined>(qNum('landValue'));
    const [landAcquisitionCost, setLandAcquisitionCost] = useState<number | undefined>(qNum('landAcquisitionCost'));
    const [landPurchaseDate, setLandPurchaseDate] = useState<Date | undefined>(qDate('landPurchaseDate'));
    const [landOwned, setLandOwned] = useState<boolean | undefined>(qBool('landOwned'));
    const [constructionCost, setConstructionCost] = useState<number | undefined>(qNum('constructionCost'));
    const [numberOfUnits, setNumberOfUnits] = useState<number | undefined>(qNum('numberOfUnits'));
    const [valueAsComplete, setValueAsComplete] = useState<number | undefined>(qNum('valueAsComplete'));

    // ── EXPERIENCE ───────────────────────────────────────────────────────────
    const [newConstructionCount, setNewConstructionCount] = useState<number | undefined>(qNum('newConstructionCount'));
    const [fixFlipCount, setFixFlipCount] = useState<number | undefined>(qNum('fixFlipCount'));
    const [rentalCount, setRentalCount] = useState<number | undefined>(qNum('rentalCount'));
    const [bfiPaymentIssueCount, setBfiPaymentIssueCount] = useState<number | undefined>(qNum('bfiPaymentIssueCount'));
    const [bfiRepaymentCount, setBfiRepaymentCount] = useState<number | undefined>(qNum('bfiRepaymentCount'));
    const [hasQualifiedGC, setHasQualifiedGC] = useState<boolean | undefined>(qBool('hasQualifiedGC'));

    // ── MARKET ───────────────────────────────────────────────────────────────
    const [affordability, setAffordability] = useState<Affordability | undefined>(qEnum<Affordability>('affordability'));
    const [appraisalFreeOfRedFlags, setAppraisalFreeOfRedFlags] = useState<boolean | undefined>(qBool('appraisalFreeOfRedFlags'));
    const [populationDensity, setPopulationDensity] = useState<PopDensity | undefined>(qEnum<PopDensity>('populationDensity'));
    const [populationGrowth, setPopulationGrowth] = useState<PopGrowth | undefined>(qEnum<PopGrowth>('populationGrowth'));
    const [preSoldUnderContract, setPreSoldUnderContract] = useState<boolean | undefined>(qBool('preSoldUnderContract'));
    const [preSaleDepositPct, setPreSaleDepositPct] = useState<number | undefined>(qNum('preSaleDepositPct'));

    // ── FINANCIAL STRENGTH ───────────────────────────────────────────────────
    const [bankBalance, setBankBalance] = useState<number | undefined>(qNum('bankBalance'));
    const [hasCriminalConviction, setHasCriminalConviction] = useState<boolean | undefined>(qBool('hasCriminalConviction') ?? false);
    const [hasLitigation, setHasLitigation] = useState<boolean | undefined>(qBool('hasLitigation') ?? false);
    const [hasOutstandingLiens, setHasOutstandingLiens] = useState<boolean | undefined>(qBool('hasOutstandingLiens') ?? false);
    const [hasRealEstateDelinquency, setHasRealEstateDelinquency] = useState<boolean | undefined>(qBool('hasRealEstateDelinquency') ?? false);
    const [hasSupplierDelinquency, setHasSupplierDelinquency] = useState<boolean | undefined>(qBool('hasSupplierDelinquency') ?? false);
    const [hasForeclosure, setHasForeclosure] = useState<boolean | undefined>(qBool('hasForeclosure') ?? false);
    const [hasBankruptcy, setHasBankruptcy] = useState<boolean | undefined>(qBool('hasBankruptcy') ?? false);
    const [hasBfiPaymentIssue, setHasBfiPaymentIssue] = useState<boolean | undefined>(qBool('hasBfiPaymentIssue') ?? false);

    // ── GUARANTOR SUMMARY ────────────────────────────────────────────────────
    const [netWorth, setNetWorth] = useState<number | undefined>(qNum('netWorth'));
    const [creditScore, setCreditScore] = useState<number | undefined>(qNum('creditScore'));

    const stateUnavailable = UNAVAILABLE_STATES.includes(state || '');

    // ── Live calculation ──────────────────────────────────────────────────────
    const result = useMemo(() => {
        if (
            !state ||
            stateUnavailable ||
            !valueAsComplete ||
            !constructionCost ||
            !numberOfUnits ||
            landOwned === undefined ||
            affordability === undefined ||
            appraisalFreeOfRedFlags === undefined ||
            populationDensity === undefined ||
            populationGrowth === undefined ||
            preSoldUnderContract === undefined ||
            hasQualifiedGC === undefined
        ) return null;

        const input: EstimatorInput = {
            state,
            landValue:            landValue ?? 0,
            landAcquisitionCost:  landAcquisitionCost ?? 0,
            landPurchaseDate,
            landOwned,
            constructionCost,
            numberOfUnits,
            valueAsComplete,
            newConstructionCount: newConstructionCount ?? 20, // Excel S29: ISBLANK → 20
            fixFlipCount:         fixFlipCount ?? 0,
            rentalCount:          rentalCount ?? 0,
            bfiPaymentIssueCount: bfiPaymentIssueCount ?? 0,
            bfiRepaymentCount:    bfiRepaymentCount ?? 0,
            hasQualifiedGC,
            affordability,
            appraisalFreeOfRedFlags,
            populationDensity,
            populationGrowth,
            preSoldUnderContract,
            preSaleDepositPct: preSoldUnderContract ? (preSaleDepositPct ?? 0) / 100 : null,
            bankBalance:  bankBalance ?? 0,
            hasCriminalConviction: hasCriminalConviction ?? false,
            hasLitigation: hasLitigation ?? false,
            hasOutstandingLiens: hasOutstandingLiens ?? false,
            hasRealEstateDelinquency: hasRealEstateDelinquency ?? false,
            hasSupplierDelinquency: hasSupplierDelinquency ?? false,
            hasForeclosure: hasForeclosure ?? false,
            hasBankruptcy: hasBankruptcy ?? false,
            hasBfiPaymentIssue: hasBfiPaymentIssue ?? false,
            netWorth:     netWorth ?? 0,
            creditScore:  creditScore ?? 0,
        };

        return calculateMaxLoan(input);
    }, [
        state, stateUnavailable, landValue, landAcquisitionCost, landPurchaseDate,
        landOwned, constructionCost, numberOfUnits, valueAsComplete,
        newConstructionCount, fixFlipCount, rentalCount, bfiPaymentIssueCount,
        bfiRepaymentCount, hasQualifiedGC, affordability, appraisalFreeOfRedFlags,
        populationDensity, populationGrowth, preSoldUnderContract, preSaleDepositPct,
        bankBalance, hasCriminalConviction, hasLitigation, hasOutstandingLiens,
        hasRealEstateDelinquency, hasSupplierDelinquency, hasForeclosure,
        hasBankruptcy, hasBfiPaymentIssue, netWorth, creditScore,
    ]);

    const experiencePoints =
        [newConstructionCount, fixFlipCount, rentalCount, bfiRepaymentCount].reduce((sum, n) => sum + (n ?? 0), 0);
    const qualificationsMet = experiencePoints >= 2 || hasQualifiedGC === true;

    const nonZeroAdj = result?.adjustments.filter((a) => a.value !== 0) ?? [];

    const nonBlankNonZeroMessage = 'Must not be blank or 0';

    return (
        <>
            {/* Style overrides for admin version */}
            <style type="text/css">
            {`
                .MuiPaper-elevation0:has(.est-page) {
                    overflow-y: auto !important;
                }
                .MuiPaper-elevation0 .est-results {
                    top: 20px;
                    margin-bottom: 20px 
                }
                .est-outer-admin {
                    h3.est-section-head {
                        font-size: 1.1rem !important;
                        margin-top: 30px !important;
                        &:first-of-type {
                            margin-top: 0 !important;
                        }
                    }
                    .dx-texteditor-input-container input {
                        /* Remove inner border/shadow from input fields (already have outer rounded border) */
                        border: none !important;
                        box-shadow: none !important;
                    }
                    .togglebuttongroup-input .dx-button-text {
                        /* Yes/No toggle button font face (defaults to generic serif) */
                        font-family: "Helvetica Neue","Segoe UI",helvetica,verdana,sans-serif !important;
                    }
                    .checkbox-button-content input {
                        margin: 6px !important;
                    }
                    .checkbox-button-content span {
                        font-weight: unset !important;
                    }
                }
            `}
            </style>
            <div className='est-page'>
                <h2>BFI Loan Estimator</h2>
                <p className='est-subtitle'>Indicative only — not a loan commitment.</p>

                <MobileLoanBar result={result} qualificationsMet={qualificationsMet} />

                <div className='est-layout'>

                    {/* ── LEFT: input form ─────────────────────────────── */}
                    <div className='est-form-card'>

                        <h3 className='est-section-head'>Property</h3>

                        <DropDownBox
                            id='state'
                            value={state}
                            onChange={(value) => setState(value)}
                            label='State'
                            required={true}
                            dataSource={usaStates}
                            valueExpr='abbreviation'
                            displayExpr={isMobile ? 'abbreviation' : 'name'}
                            width='100%'
                        />

                        {stateUnavailable && (
                            <div className='est-unavailable'>
                                Loans are not available in North Dakota, Hawaii, or Alaska.
                            </div>
                        )}

                        <MoneyBox
                            id='landValue'
                            label='Value of Land'
                            value={landValue}
                            onChange={(v) => setLandValue(v ?? undefined)}
                            width='100%'
                            liveUpdate
                        />

                        <MoneyBox
                            id='landAcquisitionCost'
                            label='Land Acquisition Cost'
                            value={landAcquisitionCost}
                            onChange={(v) => setLandAcquisitionCost(v ?? undefined)}
                            width='100%'
                            liveUpdate
                        />

                        <DateTimeBox
                            id='landPurchaseDate'
                            label='Land Purchase Date'
                            value={landPurchaseDate}
                            onChange={(v) => setLandPurchaseDate(getValidDateOrUndefined(v))}
                            width='100%'
                        />

                        <YesNoToggleButton
                            id='landOwned'
                            label='Own land without seller financing?*'
                            value={landOwned}
                            onItemClick={ynClick(setLandOwned)}
                            width='230px'
                            labelSize={ToggleButtonLabelSize.Small}
                        />

                        <MoneyBox
                            id='constructionCost'
                            label='Construction Cost'
                            value={constructionCost}
                            required={true}
                            requiredMessage={nonBlankNonZeroMessage}
                            validateValue={(v) => Number(v) > 0}
                            validateValueMessage={nonBlankNonZeroMessage}
                            onChange={(v) => setConstructionCost(v ?? undefined)}
                            width='100%'
                            liveUpdate
                        />

                        <NumberBox
                            id='numberOfUnits'
                            label='Sellable Units'
                            value={numberOfUnits}
                            required={true}
                            requiredMessage={nonBlankNonZeroMessage}
                            validateValue={(v) => Number(v) > 0}
                            validateValueMessage={nonBlankNonZeroMessage}
                            format='#0'
                            onChange={(v) => setNumberOfUnits(v ?? undefined)}
                            width='100%'
                            liveUpdate
                        />

                        <MoneyBox
                            id='valueAsComplete'
                            label='Value as Complete (Appraised)'
                            value={valueAsComplete}
                            required={true}
                            requiredMessage={nonBlankNonZeroMessage}
                            validateValue={(v) => Number(v) > 0}
                            validateValueMessage={nonBlankNonZeroMessage}
                            onChange={(v) => setValueAsComplete(v ?? undefined)}
                            width='100%'
                            liveUpdate
                        />

                        <h3 className='est-section-head'>Experience</h3>

                        <NumberBox
                            id='newConstructionCount'
                            label='New Construction completed, last 10 yrs'
                            value={newConstructionCount}
                            format='#0'
                            onChange={(v) => setNewConstructionCount(v ?? undefined)}
                            width='100%'
                            liveUpdate
                        />

                        <NumberBox
                            id='fixFlipCount'
                            label='Fix-Flip completed, last 10 yrs'
                            value={fixFlipCount}
                            format='#0'
                            onChange={(v) => setFixFlipCount(v ?? undefined)}
                            width='100%'
                            liveUpdate
                        />

                        <NumberBox
                            id='rentalCount'
                            label='Rental Properties, last 10 yrs'
                            value={rentalCount}
                            format='#0'
                            onChange={(v) => setRentalCount(v ?? undefined)}
                            width='100%'
                            liveUpdate
                        />

                        <NumberBox
                            id='bfiPaymentIssueCount'
                            label='BFI Payment Issues'
                            value={bfiPaymentIssueCount}
                            format='#0'
                            onChange={(v) => setBfiPaymentIssueCount(v ?? undefined)}
                            width='100%'
                            liveUpdate
                        />

                        <NumberBox
                            id='bfiRepaymentCount'
                            label='BFI Repayments, last 5 yrs'
                            value={bfiRepaymentCount}
                            format='#0'
                            onChange={(v) => setBfiRepaymentCount(v ?? undefined)}
                            width='100%'
                            liveUpdate
                        />

                        <YesNoToggleButton
                            id='hasQualifiedGC'
                            label='Qualified 3rd-Party General Contractor?*'
                            value={hasQualifiedGC}
                            onItemClick={ynClick(setHasQualifiedGC)}
                            width='230px'
                            labelSize={ToggleButtonLabelSize.Small}
                        />

                        <h3 className='est-section-head'>Market</h3>

                        <DropDownBox
                            id='affordability'
                            label='Affordability'
                            value={affordability}
                            required={true}
                            onChange={(v) => setAffordability(v ?? undefined)}
                            dataSource={AFFORDABILITY_OPTIONS}
                            valueExpr='id'
                            displayExpr='label'
                            width='100%'
                        />

                        <YesNoToggleButton
                            id='appraisalFreeOfRedFlags'
                            label='Appraisal free of red flags?*'
                            value={appraisalFreeOfRedFlags}
                            onItemClick={ynClick(setAppraisalFreeOfRedFlags)}
                            width='230px'
                            labelSize={ToggleButtonLabelSize.Small}
                        />

                        <DropDownBox
                            id='populationDensity'
                            label='Population in 3-mile radius'
                            value={populationDensity}
                            required={true}
                            onChange={(v) => setPopulationDensity(v ?? undefined)}
                            dataSource={POP_DENSITY_OPTIONS}
                            valueExpr='id'
                            displayExpr='label'
                            width='100%'
                        />

                        <DropDownBox
                            id='populationGrowth'
                            label='Population growth, next 5 yrs'
                            value={populationGrowth}
                            required={true}
                            onChange={(v) => setPopulationGrowth(v ?? undefined)}
                            dataSource={POP_GROWTH_OPTIONS}
                            valueExpr='id'
                            displayExpr='label'
                            width='100%'
                        />

                        <YesNoToggleButton
                            id='preSoldUnderContract'
                            label='Pre-Sold and Under Contract?*'
                            value={preSoldUnderContract}
                            onItemClick={ynClick(setPreSoldUnderContract)}
                            width='230px'
                            labelSize={ToggleButtonLabelSize.Small}
                        />

                        {preSoldUnderContract && (
                            <NumberBox
                                id='preSaleDepositPct'
                                label='Earnest Money Deposit %'
                                value={preSaleDepositPct}
                                max={100}
                                format='#0.0'
                                onChange={(v) => setPreSaleDepositPct(v ?? undefined)}
                                width='100%'
                                liveUpdate
                            />
                        )}

                        <h3 className='est-section-head'>Financial Strength</h3>

                        <MoneyBox
                            id='bankBalance'
                            label='Bank Account 2-Month Avg Balance'
                            value={bankBalance}
                            onChange={(v) => setBankBalance(v ?? undefined)}
                            width='100%'
                            liveUpdate
                        />

                        <h4 className='est-section-head est-section-head--sub'>
                            Background — Company &amp; 20%+ Owners have:
                        </h4>

                        <div className='est-flag-list'>
                            <CheckButton
                                id='f-criminal'
                                label='Criminal or felony conviction'
                                selected={!!hasCriminalConviction}
                                onToggle={() => setHasCriminalConviction((v) => !v)}
                            />
                            <CheckButton
                                id='f-litigation'
                                label='Litigation with financial institution in last 12 months'
                                selected={!!hasLitigation}
                                onToggle={() => setHasLitigation((v) => !v)}
                            />
                            <CheckButton
                                id='f-liens'
                                label='Outstanding real estate liens'
                                selected={!!hasOutstandingLiens}
                                onToggle={() => setHasOutstandingLiens((v) => !v)}
                            />
                            <CheckButton
                                id='f-re-delinq'
                                label='30+ days delinquent on real estate loan'
                                selected={!!hasRealEstateDelinquency}
                                onToggle={() => setHasRealEstateDelinquency((v) => !v)}
                            />
                            <CheckButton
                                id='f-supplier'
                                label='30+ days delinquent to primary building supplier'
                                selected={!!hasSupplierDelinquency}
                                onToggle={() => setHasSupplierDelinquency((v) => !v)}
                            />
                            <CheckButton
                                id='f-foreclosure'
                                label='Foreclosure within past 10 years'
                                selected={!!hasForeclosure}
                                onToggle={() => setHasForeclosure((v) => !v)}
                            />
                            <CheckButton
                                id='f-bankruptcy'
                                label='Bankruptcy within past 10 years'
                                selected={!!hasBankruptcy}
                                onToggle={() => setHasBankruptcy((v) => !v)}
                            />
                            <CheckButton
                                id='f-bfi-issue'
                                label='BFI payment issue in past 12 months'
                                selected={!!hasBfiPaymentIssue}
                                onToggle={() => setHasBfiPaymentIssue((v) => !v)}
                            />
                        </div>

                        <h3 className='est-section-head'>Guarantor Summary</h3>

                        <MoneyBox
                            id='netWorth'
                            label='Net Worth, excluding primary residences'
                            value={netWorth}
                            onChange={(v) => setNetWorth(v ?? undefined)}
                            width='100%'
                            liveUpdate
                        />

                        <NumberBox
                            id='creditScore'
                            label='Credit Score'
                            value={creditScore}
                            format='#0'
                            onChange={(v) => setCreditScore(v ?? undefined)}
                            width='100%'
                            liveUpdate
                        />

                    </div>

                    {/* ── RIGHT: results panel ──────────────────────────── */}
                    <ResultsPanel result={result} nonZeroAdj={nonZeroAdj} showFullResults={showFullResults} qualificationsMet={qualificationsMet} />

                </div>
            </div>
        </>
    );
};
